import React, { useState, useEffect, useCallback } from "react";

//import localization
import { useTranslation } from "react-i18next";

import { Row, Col, List, Statistic, Divider, Modal, Button } from "antd";
import { FileProtectOutlined, FileDoneOutlined, HomeOutlined, FlagOutlined, FieldTimeOutlined, UserOutlined, CheckOutlined, CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import http from "../utils/http";
import { DatePatterns } from "../utils/consts";
import GeneralErrorMsg from "../utils/GeneralErrorMsg";

import { stateContext } from "./MainView";

const { confirm } = Modal;

function SummaryView({ query }) {
	//Locale Hooks
	const { t } = useTranslation();

	const [formState, setFormState] = React.useContext(stateContext);
	const [loadingState, setLoadingState] = useState(false);

	//Const used for code simplification only to avoid nested objects
	const Travellers = formState?.TravellersForm?.Travellers;
	const Contractor = formState?.TravellersForm?.Contractor;
	const Product = formState?.ProductForm;
	const Plan = formState?.SelectedPlan;

	//On init set scroll position to top
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	//Function that renders a modal in order to notify the user that he will be unable to change their inputs after this step
	//User clicks yes: a post call is triggered in order to /acceptoffer
	//User click no: the modal gets destroyed
	const onPaymentProceed = useCallback(() => {
		confirm({
			icon: <ExclamationCircleOutlined />,
			title: t("Warning"),
			content: <div>{t("WarningContentSummary")}</div>,
			cancelText: t("No"),
			okText: t("Yes"),
			centered: true,
			onOk() {
				setLoadingState(true);
				const paymentInfo = {
					ProductId: Product.Product.ID,
					PlanGroupId: Product.PlanGroup.ID,
					Start: Product.Duration[0].format(),
					End: Product.Duration[1].format(),
					ZoneId: Product.Destination.ZoneId,
					DestinationId: Product.Destination.ID,
					DepartureId: Product.Departure.ID,
					Items: Travellers.map((item) => {
						return { Birth: item.BirthDate.format(), FirstName: item.FirstName, LastName: item.LastName, IsMale: item.Gender, Money: item.Money };
					}),
					PlanId: Plan.ID,
					ItemId: Plan.ItemID,
					SubItemId: Plan.SubItemID,

					Contractor: {
						FirstName: Contractor.FirstName,
						LastName: Contractor.LastName,
						IsMale: Contractor.Gender,
						Birth: Contractor.Birth,
						IdTypeId: JSON.parse(Contractor.Certificate.IdTypeId).ID,
						IdNo: Contractor.Certificate.IdNo,
						Email: Contractor.Email,
						Phone: `${Contractor.Mobile.Prefix}${Contractor.Mobile.Number}`,
						CountryId: JSON.parse(Contractor.Location.Country).ID,
						City: Contractor.Location.City,
						ZipCode: Contractor.ZipCode,
						Street: Contractor.Street
					}
				};

				http
					.post(`/travelinsurancesingle/acceptoffer?ch=${query.Channel}&lg=${query.Language}`, paymentInfo)
					.then((response) => {
						setLoadingState(false);
						if (response.data.success)
							setFormState((formState) => {
								return { ...formState, CurrentStep: formState.CurrentStep + 1, AcceptOffer: response.data };
							});
						else {
							console.log(response.data.errormsg);
							GeneralErrorMsg({ title: t("ErrorMessageTitle"), content: t("ErrorMessageContent") });
						}
					})
					.catch((error) => {
						setLoadingState(false);
						console.log(error);
						GeneralErrorMsg({ title: t("ErrorMessageTitle"), content: t("ErrorMessageContent") });
					});
			}
		});
	}, [Product, Travellers, Plan, Contractor, setLoadingState, setFormState, query, t]);

	return (
		<div style={{ padding: 24 }}>
			<h2>{t("Travellers")}</h2>
			<List
				itemLayout="horizontal"
				size="small"
				dataSource={formState?.TravellersForm?.Travellers}
				renderItem={(traveller) => (
					<List.Item>
						<List.Item.Meta
							title={
								<>
									<span>
										{traveller.FirstName} {traveller.LastName}
									</span>
									<span className="ant-list-item-meta-description">
										{" - "}
										{traveller.BirthDate.format("DD/MM/YYYY")}
									</span>
								</>
							}
						/>
					</List.Item>
				)}
			/>
			<Divider style={{ margin: "14px 0px" }} />
			<h2>{t("PolicyHolderDetails")}</h2>
			<Row gutter={[12, 6]}>
				<Col xs={24} sm={12} lg={12}>
					{t("FullName")}: {Contractor?.FirstName} {Contractor?.LastName}
					<br />
					{t("BirthDate")}: {Contractor?.Birth?.format(DatePatterns.LongDate)}
				</Col>
				<Col xs={24} sm={12} lg={12}>
					{t("CertType")}: {JSON.parse(Contractor.Certificate.IdTypeId).Name}
					<br />
					{t("CertNumber")}: {Contractor.Certificate.IdNo}
				</Col>

				<Col xs={24} sm={12} lg={12}>
					{t("Country")}: {JSON.parse(Contractor.Location.Country).Name}
					<br />
					{t("City")}: {Contractor.Location.City}
					<br />
					{t("Street")}: {Contractor.Street}
					<br />
					{t("PostalCode")}: {Contractor.ZipCode}
				</Col>
				<Col xs={24} sm={12} lg={12}>
					{t("MobileNumber")}: {Contractor.Mobile.Prefix} {Contractor.Mobile.Number}
					<br />
					{t("EmailAddress")}: {Contractor.Email}
				</Col>
			</Row>

			<Divider />
			<Row justify="space-between" gutter={[12, 6]}>
				<Col xs={24} sm={12} lg={12}>
					<h2>{t("TripDetails")}</h2>
					<div>
						<FileProtectOutlined /> {Product.Product.Name}
					</div>
					<div>
						<HomeOutlined /> {Product.Departure.Name}
					</div>
					<div>
						<FlagOutlined /> {Product.Destination.Name}
					</div>
					<div>
						<FieldTimeOutlined /> {Product.Duration[0].format(DatePatterns.LongDate)} - {Product.Duration[1].format(DatePatterns.LongDate)}
					</div>
					<div>
						<UserOutlined /> {Product.Travellers.length}
					</div>
				</Col>
				<Col xs={24} sm={12} lg={12}>
					<h2>{t("PlanSingle")}</h2>
					<div>
						<FileDoneOutlined />
						&nbsp;&nbsp;{Plan.Name}
					</div>
					<table style={{ width: "100%" }}>
						<tbody>
							{Plan.Covers.map((content) => (
								<tr key={content.ID}>
									<td style={{ display: "block", paddingRight: 5 }}>{content.Supported ? <CheckOutlined style={{ color: "green" }} /> : <CloseOutlined style={{ color: "red" }} />}</td>
									<td>{content.Name}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Col>
			</Row>
			<Divider />
			<Statistic value={Plan.MandatoryGrossAmount} valueStyle={{ color: "black", textAlign: "center", fontSize: 30, fontWeight: "bold" }} suffix="€" />
			<h2 style={{ textAlign: "center", marginTop: -10 }}>{t("Total")}</h2>

			<div className="cost-secondary">
				{t("TaxPremium")} {Plan.MandatoryTaxAmount}€
			</div>
			<Divider />
			<Button type="primary" block loading={loadingState} onClick={onPaymentProceed}>
				{t("ProceedToPayment")}
			</Button>
		</div>
	);
}

export default React.memo(SummaryView);
